/** Node pools: templates a provider turns into machines, scaled up when missions wait and down when idle. */
import { Boxes, Cloud, Info, Pencil, Plus, Trash2 } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import { INSTANCE_STATE_LABEL, liveInstance, parseLabels, poolInputError } from '@orgus/protocol';
import type { Id, Machine, NodePool, PoolInput, PoolInstance, SchedulerState } from '@orgus/protocol';
import { Button, ConfirmDialog, Dialog, EmptyState, ErrorState, IconButton, Input, Panel, REVEAL_GROUP, RelativeTime, RevealSlot, RowStatus, Select, Tooltip, cn, formatInt, toast } from '@orgus/ui';
import type { RowStatusProps } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { FactGrid } from '../detail/FactGrid.tsx';
import { createPool, deletePool, updatePool } from './data.ts';

/** A pool machine's state, plain as in every row: only starting and running work is coloured. */
const INSTANCE_STATUS: Record<PoolInstance['state'], Pick<RowStatusProps, 'tone' | 'icon'>> = {
  provisioning: { tone: 'working', icon: 'working' },
  running: { tone: 'working', icon: 'working' },
  stopping: { tone: 'neutral', icon: 'stopped' },
  gone: { tone: 'neutral', icon: 'ended' },
  failed: { tone: 'danger', icon: 'failed' },
};

function PoolDialog({ open, onOpenChange, state, pool }: { open: boolean; onOpenChange: (v: boolean) => void; state: SchedulerState; pool: NodePool | null }) {
  const provider0 = state.providers[0];
  const [f, setF] = useState({ name: '', provider: '', image: '', size: '', labels: '', min: '0', max: '2', idle: '15' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  useEffect(() => {
    if (!open) return;
    setF(
      pool
        ? { name: pool.name, provider: pool.provider, image: pool.image, size: pool.size, labels: pool.labels.join(', '), min: String(pool.min), max: String(pool.max), idle: String(pool.idleTimeoutMin) }
        : { name: '', provider: provider0?.id ?? '', image: provider0?.images[0] ?? '', size: provider0?.sizes[0]?.id ?? '', labels: 'linux', min: '0', max: '2', idle: '15' },
    );
    setError(null);
    setBusy(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const provider = state.providers.find((p) => p.id === f.provider);
  const body: PoolInput = { name: f.name.trim(), provider: f.provider, image: f.image.trim(), size: f.size, labels: parseLabels(f.labels), min: Number(f.min), max: Number(f.max), idleTimeoutMin: Number(f.idle) };
  const invalid = poolInputError(body, state.providers);
  const submit = async () => {
    if (invalid || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (pool) {
        const { provider: _p, ...patch } = body;
        await updatePool(pool.id, patch);
        toast.success(`Saved ${body.name}`);
      } else {
        await createPool(body);
        toast.success(`Created ${body.name}`, body.min ? `${body.min} machines start now.` : 'Machines start when missions wait for its labels.');
      }
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={pool ? `Edit ${pool.name}` : 'New node pool'}
      description="A template for machines: where they come from, what they boot, their size and labels, and how many."
      size="lg"
      dismissible={!busy}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} disabled={!!invalid} onClick={() => void submit()}>
            {pool ? 'Save pool' : 'Create pool'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Name" hint="Machines are named after it: letters, digits and dashes." value={f.name} onChange={(e) => set('name')(e.target.value)} placeholder="gpu-linux" maxLength={40} />
          <Select label="Provider" value={f.provider} onValueChange={set('provider')} disabled={!!pool} options={state.providers.map((p) => ({ value: p.id, label: p.label }))} />
          <Select label="Image" value={f.image} onValueChange={set('image')} options={(provider?.images ?? [f.image]).map((i) => ({ value: i, label: i }))} />
          <Select label="Size" value={f.size} onValueChange={set('size')} options={(provider?.sizes ?? []).map((s) => ({ value: s.id, label: s.label }))} />
        </div>
        <Input label="Labels" value={f.labels} onChange={(e) => set('labels')(e.target.value)} hint="Missions that need these labels make the pool grow." className="font-mono" />
        <div className="grid grid-cols-3 gap-3">
          <Input label="Minimum" inputMode="numeric" value={f.min} onChange={(e) => set('min')(e.target.value)} hint="Always running." />
          <Input label="Maximum" inputMode="numeric" value={f.max} onChange={(e) => set('max')(e.target.value)} hint="Never more." />
          <Input label="Idle timeout (min)" inputMode="numeric" value={f.idle} onChange={(e) => set('idle')(e.target.value)} hint="Then removed, above the minimum." />
        </div>
        {invalid && f.name && <p className="text-caption text-failed">{invalid}</p>}
        {provider && !provider.real && (
          <p className="flex items-start gap-2 rounded-control border border-border-subtle bg-raised px-3 py-2 text-caption text-fg-muted">
            <Info className="mt-1 size-4 shrink-0" aria-hidden />
            The fake provider simulates machines in the hub: nothing is created in any cloud and nothing is billed. Its machines never join, so they add no real capacity.
          </p>
        )}
        {error && <ErrorState variant="inline" error={error} />}
      </div>
    </Dialog>
  );
}

/** How far a pool scales and how many of its machines are up: "0 to 2, 1 running". */
const scale = (p: NodePool, live: number) => `${formatInt(p.min)} to ${formatInt(p.max)}, ${formatInt(live)} running`;

export function PoolsTab({ state, machines }: { state: SchedulerState; machines: Record<Id, Machine> }) {
  const [editing, setEditing] = useState<NodePool | null | 'new'>(null);
  const [deleting, setDeleting] = useState<NodePool | null>(null);
  const sizeLabel = (p: NodePool) => state.providers.find((x) => x.id === p.provider)?.sizes.find((s) => s.id === p.size)?.label ?? p.size;
  const providerLabel = (id: string) => state.providers.find((x) => x.id === id)?.label ?? `${id} (not configured)`;
  const realProviders = state.providers.filter((p) => p.real);

  return (
    <div className="flex flex-col gap-4">
      {!realProviders.length && (
        <div className="flex items-center gap-3 rounded-card border border-border bg-surface px-4 py-2 text-caption">
          <Cloud className="size-4 shrink-0 text-fg-subtle" aria-hidden />
          <Tooltip content="Real machines need a provider (Hetzner, AWS, GCP or Fly) with your credentials, which Orgus never adds on its own">
            <span tabIndex={0} className="text-fg-muted">
              Only the simulated provider is set up: pools scale, but no real machine joins
            </span>
          </Tooltip>
        </div>
      )}
      <Panel
        title="Pools"
        sub={state.pools.length ? `${formatInt(state.pools.length)} ${state.pools.length === 1 ? 'pool' : 'pools'}` : undefined}
        icon={<Boxes />}
        flush
        actions={
          <Button size="sm" variant="secondary" icon={<Plus />} onClick={() => setEditing('new')}>
            New pool
          </Button>
        }
      >
        {state.pools.length ? (
          <ul>
            {state.pools.map((p) => {
              const mine = state.instances.filter((i) => i.poolId === p.id);
              const live = mine.filter(liveInstance);
              // A simulated provider's message repeats the banner above: only a failure's says something new.
              const real = state.providers.find((x) => x.id === p.provider)?.real ?? true;
              const note = (i: PoolInstance) => (i.machineId ? `Joined as ${machines[i.machineId]?.name ?? 'a removed machine'}` : real || i.state === 'failed' ? (i.message ?? null) : null);
              return (
                <li key={p.id} className={cn(REVEAL_GROUP, 'flex flex-col gap-3 border-b border-border-subtle px-4 py-3 last:border-b-0')}>
                  <div className="flex min-h-control-sm min-w-0 items-center gap-2">
                    <span className="min-w-0 truncate font-medium text-fg">{p.name}</span>
                    <RevealSlot className="ml-auto flex shrink-0 items-center gap-1">
                      <IconButton label={`Edit ${p.name}`} icon={<Pencil />} size="sm" onClick={() => setEditing(p)} />
                      <IconButton label={`Delete ${p.name}`} icon={<Trash2 />} variant="danger" size="sm" onClick={() => setDeleting(p)} />
                    </RevealSlot>
                  </div>
                  {/* Desktop: the detail panels' fact grid. A phone's cells are too narrow for a
                      provider, a size or an image, so there the long facts take the full width. */}
                  <FactGrid
                    className="hidden sm:flex"
                    label={`${p.name} settings`}
                    lines={[
                      [
                        { label: 'Provider', value: providerLabel(p.provider), title: providerLabel(p.provider) },
                        { label: 'Size', value: sizeLabel(p), title: sizeLabel(p) },
                        { label: 'Scale', value: scale(p, live.length) },
                        { label: 'Idle timeout', value: `${formatInt(p.idleTimeoutMin)} min` },
                      ],
                      [
                        { label: 'Labels', value: p.labels.length ? p.labels.join(', ') : 'None', mono: p.labels.length > 0 },
                        { label: 'Image', value: p.image, title: p.image, mono: true },
                      ],
                    ]}
                  />
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:hidden" aria-label={`${p.name} settings`}>
                    {(
                      [
                        ['Provider', providerLabel(p.provider), true, false],
                        ['Size', sizeLabel(p), true, false],
                        ['Scale', scale(p, live.length), false, false],
                        ['Idle timeout', `${formatInt(p.idleTimeoutMin)} min`, false, false],
                        ['Labels', p.labels.length ? p.labels.join(', ') : 'None', true, p.labels.length > 0],
                        ['Image', p.image, true, true],
                      ] as const
                    ).map(([k, v, wide, mono]) => (
                      <div key={k} className={cn('flex min-w-0 flex-col gap-1', wide && 'col-span-2')}>
                        <dt className="text-caption text-fg-subtle">{k}</dt>
                        <dd className={cn('min-w-0 break-words text-fg-muted num', mono ? 'font-mono text-caption' : 'text-ui')}>{v}</dd>
                      </div>
                    ))}
                  </dl>
                  {mine.length > 0 && (
                    <ul className="flex flex-col rounded-control border border-border-subtle bg-raised px-3 py-1" aria-label={`Machines of ${p.name}`}>
                      {mine.slice(0, 6).map((i) => {
                        const st = INSTANCE_STATUS[i.state];
                        const said = note(i);
                        return (
                          <li key={i.id} className="flex min-h-row min-w-0 flex-wrap items-center gap-x-3 py-1 text-caption">
                            <RowStatus {...st} label={INSTANCE_STATE_LABEL[i.state]} focusable={false} className="w-24 text-caption" />
                            <span className="min-w-0 truncate font-mono text-fg-muted">{i.name}</span>
                            {said && <span className="order-last w-full truncate text-fg-subtle sm:order-none sm:w-auto sm:min-w-0 sm:flex-1 sm:pl-0">{said}</span>}
                            <span className="ml-auto shrink-0 text-fg-subtle num">
                              <RelativeTime ts={i.state === 'gone' || i.state === 'failed' ? i.updatedAt : i.createdAt} />
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <EmptyState
            icon={<Boxes />}
            title="No node pools"
            description="A machine template that grows when missions wait for its labels."
            action={
              <Button variant="primary" onClick={() => setEditing('new')}>
                New pool
              </Button>
            }
          />
        )}
      </Panel>
      <PoolDialog open={editing != null} onOpenChange={(v) => !v && setEditing(null)} state={state} pool={editing === 'new' ? null : editing} />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        title={`Delete pool ${deleting?.name ?? ''}?`}
        description="A pool with machines is refused: set its maximum to 0 first."
        confirmLabel="Delete pool"
        onConfirm={async () => {
          if (!deleting) return;
          await deletePool(deleting.id);
          toast.success(`Deleted ${deleting.name}`);
        }}
      />
    </div>
  );
}
